import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Loader2, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { useAuth } from './AuthProvider.jsx';
import { APP_NAME } from '@/lib/constants.js';

/**
 * Route guards.
 *
 * Both are **courtesies, not enforcement**. The API is the enforcement: every
 * `/admin/*` route authenticates the operator token and the super-admin-only
 * ones answer 403 regardless of what the client renders. These exist so an
 * operator is not shown a page that cannot load.
 *
 * Worth being explicit about, because a guard that looks like security invites
 * somebody to later move a check *out* of the API on the grounds that the panel
 * already does it.
 */

/**
 * The boot splash.
 *
 * Shown while the session is being restored from the refresh cookie. Without
 * it `ProtectedRoute` would see "no token" on the first render and bounce a
 * signed-in operator to `/login`, where the refresh would then land and
 * redirect them back — a visible flash to arrive where they already were.
 */
function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background">
      <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
      <p className="text-sm text-muted-foreground">Restoring your session…</p>
      <span className="sr-only">{APP_NAME} is loading</span>
    </div>
  );
}

/** Requires a session. Sends anonymous callers to `/login`. */
export function ProtectedRoute() {
  const { isRestoring, isAuthenticated } = useAuth();
  const location = useLocation();

  if (isRestoring) return <Splash />;

  if (!isAuthenticated) {
    /*
     * The attempted path travels in location state so the login screen can
     * return the operator to it. An operator whose token lapsed while they were
     * three filters deep into a user's stakes should land back there, not on
     * the dashboard.
     */
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

/** The inverse: keeps a signed-in operator off the login screen. */
export function PublicOnlyRoute() {
  const { isRestoring, isAuthenticated } = useAuth();

  if (isRestoring) return <Splash />;
  if (isAuthenticated) return <Navigate to="/" replace />;

  return <Outlet />;
}

/**
 * Requires `super_admin`.
 *
 * Renders an explanation rather than redirecting. A plain admin reaching one of
 * these has almost always followed a link from a colleague, and a silent bounce
 * to the dashboard reads as a broken link rather than as a permission
 * boundary.
 */
export function RoleGate({ children }) {
  const { isSuperAdmin } = useAuth();

  if (isSuperAdmin) return children ?? <Outlet />;

  return (
    <div className="flex flex-col items-center justify-center px-6 py-20 text-center">
      <div className="mb-3 rounded-full bg-warning/15 p-3">
        <ShieldAlert className="h-6 w-6 text-warning" aria-hidden="true" />
      </div>

      <h1 className="text-lg font-semibold">Super admin only</h1>

      <p className="mt-1 max-w-md text-sm text-muted-foreground">
        This section is limited to super admins. It is the record of what every operator has done.
      </p>

      <Button asChild variant="outline" size="sm" className="mt-5">
        <a href="/">Back to the dashboard</a>
      </Button>
    </div>
  );
}
